/**
 * SustainabilityConfigPanel — Iter-158 · Phase 2c.
 * Finance-only settings panel to tune sustainability multipliers.
 * Writes to /api/iter156/sustainability/config; the Impact This Quarter
 * strip re-reads the updated multipliers on its next render.
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Leaf, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';
const inputCls = 'w-full rounded-md px-3 py-2 text-sm text-white bg-[#141414] border-2 border-[#3f3f46] focus:border-[#C9A962] focus:outline-none';
const labelCls = 'block text-[10px] font-bold uppercase tracking-widest mb-1 text-[#C9A962]';

export default function SustainabilityConfigPanel() {
  const [cfg, setCfg] = useState({ lbs_per_pickup: 42, gallons_saved_per_pressure_wash: 55, landfill_diverted_base_pct: 55 });
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/iter156/sustainability/config`);
        if (r.ok) {
          const d = await r.json();
          setCfg({
            lbs_per_pickup: d.lbs_per_pickup,
            gallons_saved_per_pressure_wash: d.gallons_saved_per_pressure_wash,
            landfill_diverted_base_pct: d.landfill_diverted_base_pct,
          });
        }
      } catch (_) {}
    })();
  }, []);

  const save = async () => {
    setBusy(true); setStatus('');
    try {
      const r = await fetch(`${API}/api/iter156/sustainability/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lbs_per_pickup: Number(cfg.lbs_per_pickup),
          gallons_saved_per_pressure_wash: Number(cfg.gallons_saved_per_pressure_wash),
          landfill_diverted_base_pct: Number(cfg.landfill_diverted_base_pct),
        }),
      });
      const d = await r.json();
      if (r.ok) setStatus(`Saved at ${d.updated_at}. Sustainability strip will refresh on next load.`);
      else setStatus(`Error: ${d?.detail || r.status}`);
    } catch (e) { setStatus(`Error: ${e.message}`); }
    setBusy(false);
  };

  return (
    <Card className="p-5 bg-[#141414] border-zinc-800" data-testid="sustainability-config-panel">
      <div className="flex items-center gap-2 mb-3">
        <Leaf className="h-4 w-4" style={{ color: '#7CFC8A' }} />
        <p className="text-sm font-bold text-white uppercase tracking-widest" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sustainability Multipliers</p>
      </div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4">
        Configure the operational math that drives Impact This Quarter. Finance access only.
      </p>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Lbs per Pickup</label>
          <input type="number" step="1" min="1" className={inputCls} value={cfg.lbs_per_pickup} onChange={(e) => setCfg({ ...cfg, lbs_per_pickup: e.target.value })} data-testid="sus-config-lbs-per-pickup" />
        </div>
        <div>
          <label className={labelCls}>Gallons saved per Pressure Wash</label>
          <input type="number" step="1" min="1" className={inputCls} value={cfg.gallons_saved_per_pressure_wash} onChange={(e) => setCfg({ ...cfg, gallons_saved_per_pressure_wash: e.target.value })} data-testid="sus-config-gallons-per-wash" />
        </div>
        <div>
          <label className={labelCls}>Landfill diverted % baseline</label>
          <input type="number" step="0.1" min="0" max="100" className={inputCls} value={cfg.landfill_diverted_base_pct} onChange={(e) => setCfg({ ...cfg, landfill_diverted_base_pct: e.target.value })} data-testid="sus-config-diverted-pct" />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save} disabled={busy} className="font-bold uppercase tracking-widest" style={{ backgroundColor: GOLD, color: '#111' }} data-testid="sus-config-save">
          <Save className="h-4 w-4 mr-1" /> {busy ? 'SAVING…' : 'SAVE'}
        </Button>
        {status && <p className="text-xs" style={{ color: status.startsWith('Error') ? '#f87171' : '#7CFC8A' }} data-testid="sus-config-status">{status}</p>}
      </div>
    </Card>
  );
}
